import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Icon } from '@/shared/ui';
import './settings-d.css';
import { SectionHeader, Switch } from './primitives';
import { PayerDetailPage, renderSettingsPage } from './settingsPages';
import { getSettingsSync, setMode as setClaimsMode, useClaimsVersion } from '@/features/claims-review/store';
import { TRAINING_GOAL } from '@/features/claims-review/utils';
import { getAppealSettings, saveAppealSettings, useAppealsVersion } from '@/features/appeals/store';
import {
  PREAUTH_TRAINING_TARGET,
  getSettingsSync as getPreauthSettings,
  preauthsSentCountSync,
  toggleAutopilot as setPreauthAutopilot,
  usePreauthsVersion,
} from '@/features/preauths/store';
import { OWNER_NAME } from '@/features/preauths/utils';
import { isPracticeOwner } from '@/features/shared/permissions';

/** Appeals training goal (the Appeals board counts "of 20"). */
const APPEALS_GOAL = 20;
import {
  ALERT_CHANGE,
  ALERT_EMAIL_TIME,
  ALERT_EMAIL_TIMES,
  ALERT_PEOPLE,
  ALERT_PEOPLE_WITH_ACCESS,
  AUTOPILOT_ALWAYS,
  AUTOPILOT_CARDS,
  AUTOPILOT_DECIDES,
  AUTOPILOT_GATE_TEXT,
  isAuthorizationSigned,
  RCM_SETTINGS_CHANGE,
  RCM_SETTINGS_GROUPS,
  itemFor,
  metaFor,
  type AlertPerson,
  type AutopilotCard,
  type RcmSettingsSlug,
} from './settingsData';
import { recordAlertsChange, useAlertsSaveState } from './alertsSave';

// ── SettingsSubNav ──

function SubNav({ active }: { active: RcmSettingsSlug }) {
  return (
    <nav className="setd-nav" aria-label="Settings">
      {/* Same shape as the Core settings column: a title head, quiet section labels, easy-to-read rows. */}
      <div className="setd-nav-head">
        <div className="setd-nav-eyebrow">Settings</div>
        <h2 className="setd-nav-title">
          RCM <span>settings</span>
        </h2>
      </div>
      <div className="setd-nav-scroll">
        {RCM_SETTINGS_GROUPS.map((group) => (
          <div key={group.label} className="setd-nav-section">
            <div className="setd-nav-grp">
              <Icon kind={group.icon} size={14} aria-hidden />
              <span>{group.label}</span>
            </div>
            {group.items.map((item) => {
              const on = item.slug === active;
              return (
                <Link
                  key={item.slug}
                  to={`/settings/${item.slug}`}
                  className={`setd-nav-item${on ? ' active' : ''}`}
                  aria-current={on ? 'page' : undefined}
                >
                  <span className="setd-nav-label">{item.label}</span>
                  {item.count ? <span className="setd-nav-count">{item.count}</span> : null}
                  {on && !item.count ? <span className="setd-nav-dot" aria-hidden /> : null}
                </Link>
              );
            })}
          </div>
        ))}
      </div>
      <div className="setd-nav-foot">
        <div className="setd-nav-foot-line">
          <span className="setd-nav-foot-dot" aria-hidden />
          <span>Saved as you go</span>
        </div>
        <p>A change applies from that day on.</p>
      </div>
    </nav>
  );
}

// ── 05 Autopilot ──

const AP_PILL_LABEL: Record<AutopilotCard['pill'], string> = {
  training: 'Training',
  on: 'On',
  off: 'Off',
  ready: 'Ready',
};

function AutopilotCardView({
  card,
  liveOn,
  onToggleLive,
  lockedReason,
  confirmLead,
}: {
  card: AutopilotCard;
  /** When set, this card's switch is backed by a real shared store (D133) — no local copy. */
  liveOn?: boolean;
  onToggleLive?: (next: boolean) => void;
  /** D235: why the switch is disabled before the legal gate is cleared. */
  lockedReason?: string;
  /** First sentence of the "Turn on autopilot?" confirm. */
  confirmLead?: string;
}) {
  const [localOn, setLocalOn] = useState(card.on);
  const [confirm, setConfirm] = useState(false);
  const [toast, setToast] = useState(false);
  const on = liveOn ?? localOn;
  const pill = on ? (card.pill === 'off' ? 'on' : card.pill) : card.pill === 'on' ? 'off' : card.pill;
  const switchEnabled = card.switchEnabled && !lockedReason && !card.unavailable;

  const toggle = (next: boolean) => {
    if (next) setConfirm(true);
    else if (onToggleLive) onToggleLive(false);
    else setLocalOn(false);
  };

  return (
    <section className="setd-card">
      <SectionHeader n={card.n} title={card.title} />
      <div className="setd-ap">
        <div>
          <div className="setd-ap-status">
            <b>{card.leadBold}</b>
            {card.status}
          </div>
          {card.progress ? (
            <div className="setd-ap-bar" aria-hidden>
              <span
                style={{ width: `${String((card.progress.confirmed / card.progress.target) * 100)}%` }}
              />
            </div>
          ) : null}
          {card.unavailable ? (
            <div className="setd-note" data-testid="ap-unavailable" style={{ color: 'var(--warning)' }}>
              {card.unavailable}
            </div>
          ) : lockedReason ? (
            <div className="setd-note" data-testid="ap-gate" style={{ color: 'var(--warning)' }}>
              {lockedReason}
            </div>
          ) : null}
          <div className="setd-note">{card.note}</div>
          <div className="setd-ap-label">On autopilot, still comes to you when</div>
          <ul className="setd-ap-list">
            {card.comesToYou.map((c) => (
              <li key={c}>{c}</li>
            ))}
          </ul>
        </div>
        <div className="setd-ap-right">
          <span className={`setd-pill ${pill}`}>{AP_PILL_LABEL[pill]}</span>
          <Switch
            checked={on}
            disabled={!switchEnabled}
            label={`Autopilot for ${card.title}`}
            onChange={toggle}
          />
          <Link to={card.link.href} className="setd-ap-link">
            {card.link.label}
          </Link>
        </div>
        {card.paused && card.paused.length > 0 ? (
          <div style={{ gridColumn: '1 / -1' }} data-testid="ap-paused">
            <div className="setd-ap-label">Paused</div>
            <ul className="setd-ap-list">
              {card.paused.map((c) => (
                <li key={c}>{c}</li>
              ))}
            </ul>
            <div className="setd-note" style={{ marginTop: 6 }}>
              Turning autopilot off and on doesn’t clear a pause.
            </div>
          </div>
        ) : null}
      </div>

      {confirm ? (
        <div
          className="setd-backdrop"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) setConfirm(false);
          }}
        >
          <div className="setd-dialog" role="dialog" aria-modal="true" aria-label={`Turn on autopilot for ${card.title}?`}>
            <h2>Turn on autopilot for {card.title}?</h2>
            <div className="setd-dialog-body">
              {confirmLead ?? 'ELVA will do this work on its own.'} These still come to you:
              <ul className="setd-ap-list">
                {card.comesToYou.map((c) => (
                  <li key={c}>{c}</li>
                ))}
              </ul>
            </div>
            <div className="setd-dialog-actions">
              <button
                type="button"
                className="setd-btn setd-btn-quiet"
                onClick={() => {
                  setConfirm(false);
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                className="setd-btn setd-btn-primary"
                onClick={() => {
                  if (onToggleLive) onToggleLive(true);
                  else setLocalOn(true);
                  setConfirm(false);
                  setToast(true);
                  window.setTimeout(() => {
                    setToast(false);
                  }, 4000);
                }}
              >
                Turn on autopilot
              </button>
            </div>
          </div>
        </div>
      ) : null}
      {toast ? (
        <div className="setd-toasts">
          <div className="setd-toast" role="status">
            Autopilot is on.
          </div>
        </div>
      ) : null}
    </section>
  );
}

function AutopilotPage() {
  // D133: Claims reads one shared value (store.ts settings.confirmed / TRAINING_GOAL) —
  // this page never holds its own copy of the training count or the on/off switch.
  useClaimsVersion();
  const claimsSettings = getSettingsSync();
  const claimsOn = claimsSettings.mode === 'autopilot';
  const claimsAtGoal = claimsSettings.confirmed >= TRAINING_GOAL;

  // D133 again for Appeals: one shared value with the Denied & underpaid page.
  useAppealsVersion();
  const appeals = getAppealSettings();
  const appealsOn = appeals.autopilot;
  const appealsAtGoal = appeals.confirmed >= APPEALS_GOAL;

  // D300: pre-auth autopilot reads one shared value with the Pre-auths page; only the owner turns it on,
  // and only after 2 pre-auths have been sent through ELVA.
  usePreauthsVersion();
  const paSettings = getPreauthSettings();
  const paOn = paSettings.autopilot;
  const paSent = preauthsSentCountSync();
  const paTrained = paSent >= PREAUTH_TRAINING_TARGET;
  const owner = isPracticeOwner();
  // D235: before the claims and appeals authorization is signed, every switch is disabled (mock: signed).
  const gateReason = isAuthorizationSigned() ? undefined : AUTOPILOT_GATE_TEXT;

  const cards = AUTOPILOT_CARDS.map((card) => {
    if (card.id === 'preauths') {
      const { progress: _ignored, ...rest } = card;
      return {
        ...rest,
        on: paOn,
        switchEnabled: owner && (paTrained || paOn),
        pill: paOn ? 'on' : paTrained ? 'ready' : 'training',
        leadBold: paOn
          ? `On since ${paSettings.autopilotSince ?? 'Oct 1'}.`
          : paTrained
            ? String(PREAUTH_TRAINING_TARGET)
            : `${String(paSent)} of ${String(PREAUTH_TRAINING_TARGET)}`,
        status: paOn
          ? ` Turned on by ${paSettings.autopilotBy ?? OWNER_NAME}. Turn it off and pre-auths wait for you again.`
          : paTrained
            ? ' pre-auths submitted. Your owner can turn this on.'
            : ' pre-auths submitted. Your owner can turn this on after the second.',
        ...(!paOn && !paTrained ? { progress: { confirmed: paSent, target: PREAUTH_TRAINING_TARGET } } : {}),
      } satisfies AutopilotCard;
    }
    if (card.id === 'appeals') {
      const { progress: _ignored, ...rest } = card;
      return {
        ...rest,
        on: appealsOn,
        switchEnabled: appealsAtGoal || appealsOn,
        pill: appealsOn ? 'on' : appealsAtGoal ? 'ready' : 'training',
        leadBold: appealsOn
          ? 'On.'
          : `${String(appeals.confirmed)} of ${String(APPEALS_GOAL)}`,
        status: appealsOn
          ? ' Turn it off and appeals wait for you again.'
          : ' appeals confirmed by your team. ELVA suggests autopilot at 20.',
        ...(appealsOn ? {} : { progress: { confirmed: appeals.confirmed, target: APPEALS_GOAL } }),
      } satisfies AutopilotCard;
    }
    if (card.id !== 'claims') return card;
    const { progress: _ignoredProgress, ...rest } = card;
    return {
      ...rest,
      on: claimsOn,
      switchEnabled: claimsAtGoal || claimsOn,
      pill: claimsOn ? 'on' : claimsAtGoal ? 'ready' : 'training',
      leadBold: claimsOn
        ? `On since ${claimsSettings.autopilotSince ?? 'today'}.`
        : `${String(claimsSettings.confirmed)} of ${String(TRAINING_GOAL)}`,
      status: claimsOn
        ? ' Turn it off and new claims wait for you again.'
        : ' claims confirmed by your team. ELVA suggests autopilot at 200.',
      ...(claimsOn ? {} : { progress: { confirmed: claimsSettings.confirmed, target: TRAINING_GOAL } }),
    } satisfies AutopilotCard;
  });

  return (
    <>
      {cards.map((card) => (
        <AutopilotCardView
          key={card.id}
          card={card}
          {...(gateReason && !card.unavailable ? { lockedReason: gateReason } : {})}
          {...(card.id === 'preauths'
            ? {
                liveOn: paOn,
                confirmLead: 'ELVA will send pre-auths that are ready without waiting for you.',
                onToggleLive: (next: boolean) => {
                  setPreauthAutopilot(next);
                },
              }
            : {})}
          {...(card.id === 'appeals'
            ? {
                liveOn: appealsOn,
                onToggleLive: (next: boolean) => {
                  saveAppealSettings({ autopilot: next });
                },
              }
            : {})}
          {...(card.id === 'claims'
            ? {
                liveOn: claimsOn,
                onToggleLive: (next: boolean) => {
                  void setClaimsMode(next ? 'autopilot' : 'training');
                },
              }
            : {})}
        />
      ))}

      <section className="setd-card">
        <SectionHeader n="05" title="ELVA always does these without asking" />
        <div style={{ paddingTop: 6 }}>
          {AUTOPILOT_ALWAYS.map((r) => (
            <div key={r.lead} className="setd-lst-row">
              <span className="setd-glyph-ok" aria-hidden>
                ✓
              </span>
              <span>
                <b>{r.lead}</b>
                <span className="rest">{r.rest}</span>
              </span>
            </div>
          ))}
        </div>
      </section>

      <section className="setd-card">
        <SectionHeader n="06" title="A person always decides" />
        <div style={{ paddingTop: 6 }}>
          {AUTOPILOT_DECIDES.map((r) => (
            <div key={r.lead} className="setd-lst-row">
              <span className="setd-glyph-warn" aria-hidden>
                !
              </span>
              <span>
                <b>{r.lead}</b>
                <span className="rest">{r.rest}</span>
              </span>
            </div>
          ))}
        </div>
        <div className="setd-change">
          Only owners and admins can change these. Last change: {RCM_SETTINGS_CHANGE.by} ({RCM_SETTINGS_CHANGE.org}),{' '}
          {RCM_SETTINGS_CHANGE.date}.
        </div>
      </section>
    </>
  );
}

// ── 06 Alerts (rcm-06, email only · D169) ──

/** One person row: name 600 · role/org, mono email, Switch. No phone numbers. */
function AlertRow({
  person,
  kind,
  on,
  readOnly,
  onToggle,
}: {
  person: AlertPerson;
  kind: 'right-away' | 'morning';
  on: boolean;
  readOnly: boolean;
  onToggle: (next: boolean) => void;
}) {
  return (
    <div className="setd-row">
      <div style={{ minWidth: 0 }}>
        <div className="setd-row-title">
          {person.name}
          {/* Card 02 shows the name only; card 01 adds the role/organisation. */}
          {kind === 'right-away' ? <span className="sub"> · {person.roleOrOrg}</span> : null}
        </div>
        <div className="setd-row-help mono" title={person.email}>
          {person.email}
        </div>
      </div>
      <div className="setd-row-right">
        <Switch checked={on} disabled={readOnly} label={person.name} onChange={onToggle} />
      </div>
    </div>
  );
}

/** "Add someone" picker: people with access who aren't listed in this card yet. */
function AddSomeone({
  candidates,
  onAdd,
}: {
  candidates: AlertPerson[];
  onAdd: (id: string) => void;
}) {
  const [open, setOpen] = useState(false);
  if (candidates.length === 0) return null;
  return (
    <div style={{ position: 'relative', display: 'inline-block' }}>
      <button type="button" className="setd-ghost-btn" aria-expanded={open} onClick={() => { setOpen((v) => !v); }}>
        Add someone
      </button>
      {open ? (
        <div className="setd-picker" role="menu" aria-label="People with access">
          {candidates.map((p) => (
            <button
              key={p.id}
              type="button"
              role="menuitem"
              className="setd-picker-item"
              onClick={() => { onAdd(p.id); setOpen(false); }}
            >
              <span className="setd-row-title">{p.name}</span>
              <span className="sub"> · {p.roleOrOrg}</span>
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function AlertsPage({ readOnly }: { readOnly: boolean }) {
  // Local mock state: which listed people are in each card, their switches, the
  // send time, and the pool the picker draws from. Every change calls the mock
  // autosave (band shows Saving… then saves, writing a mock audit entry).
  const [rightAway, setRightAway] = useState<AlertPerson[]>(() => ALERT_PEOPLE.map((p) => ({ ...p })));
  const [morning, setMorning] = useState<AlertPerson[]>(() => ALERT_PEOPLE.map((p) => ({ ...p })));
  const [pool, setPool] = useState<AlertPerson[]>(() => ALERT_PEOPLE_WITH_ACCESS.map((p) => ({ ...p })));
  const [sendAt, setSendAt] = useState(ALERT_EMAIL_TIME);

  const anyRightAway = rightAway.some((p) => p.rightAway);
  const anyMorning = morning.some((p) => p.morning);

  const toggle = (
    list: AlertPerson[],
    setList: (v: AlertPerson[]) => void,
    id: string,
    field: 'rightAway' | 'morning',
    next: boolean,
    label: string,
  ): void => {
    setList(list.map((p) => (p.id === id ? { ...p, [field]: next } : p)));
    recordAlertsChange(`${next ? 'On' : 'Off'} · ${label}`);
  };

  const addTo = (field: 'rightAway' | 'morning', id: string): void => {
    const person = pool.find((p) => p.id === id);
    if (!person) return;
    const added = { ...person, [field]: true };
    if (field === 'rightAway') setRightAway((l) => [...l, added]);
    else setMorning((l) => [...l, added]);
    // Once someone is on both cards they leave the pool.
    const onOther = (field === 'rightAway' ? morning : rightAway).some((p) => p.id === id);
    if (onOther) setPool((l) => l.filter((p) => p.id !== id));
    recordAlertsChange(`Added ${person.name} · ${field === 'rightAway' ? 'right away' : 'morning'} email`);
  };

  const rightAwayCandidates = pool.filter((p) => !rightAway.some((r) => r.id === p.id));
  const morningCandidates = pool.filter((p) => !morning.some((r) => r.id === p.id));

  return (
    <>
      <section className="setd-card">
        <SectionHeader n="01" title="Right away, by email" />
        <p className="setd-desc">
          When something needs a person within the hour. For example, a dentist must approve an appeal letter before
          today&rsquo;s deadline. Not more than one email an hour per person.
        </p>
        {!anyRightAway ? (
          <p className="setd-row-warn" style={{ margin: '0 0 6px' }}>
            No one gets these emails right now. Urgent items wait in Human in the loop.
          </p>
        ) : null}
        {rightAway.map((p) => (
          <AlertRow
            key={p.id}
            person={p}
            kind="right-away"
            on={p.rightAway}
            readOnly={readOnly}
            onToggle={(next) => { toggle(rightAway, setRightAway, p.id, 'rightAway', next, p.name); }}
          />
        ))}
        {!readOnly ? <AddSomeone candidates={rightAwayCandidates} onAdd={(id) => { addTo('rightAway', id); }} /> : null}
      </section>

      <section className="setd-card">
        <SectionHeader n="02" title="Every morning, by email" />
        <p className="setd-desc">A short list of what is waiting in Human in the loop, with a link to each item.</p>
        <div className="setd-row">
          <div>
            <div className="setd-row-title">Send it at</div>
            <div className="setd-row-help">
              In the practice&rsquo;s time zone. Not sent on days nothing is waiting.
            </div>
          </div>
          <div className="setd-row-right">
            <select
              className="setd-select"
              value={sendAt}
              disabled={readOnly}
              aria-label="Send the morning email at"
              onChange={(e) => { setSendAt(e.target.value); recordAlertsChange(`Morning email at ${e.target.value}`); }}
            >
              {ALERT_EMAIL_TIMES.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
          </div>
        </div>
        {!anyMorning ? (
          <p className="setd-row-warn" style={{ margin: '0 0 6px' }}>
            No one gets the morning email.
          </p>
        ) : null}
        {morning.map((p) => (
          <AlertRow
            key={p.id}
            person={p}
            kind="morning"
            on={p.morning}
            readOnly={readOnly}
            onToggle={(next) => { toggle(morning, setMorning, p.id, 'morning', next, p.name); }}
          />
        ))}
        {!readOnly ? <AddSomeone candidates={morningCandidates} onAdd={(id) => { addTo('morning', id); }} /> : null}
      </section>

      <div className="setd-quiet">
        <b>That&rsquo;s all ELVA sends.</b> Everything else shows as counts in the sidebar and on Human in the loop. ELVA
        never emails patient details in an alert, only what kind of item it is and a link.
      </div>
      <div className="setd-change">
        Only owners and admins can change these.
        {!readOnly ? <> Last change: {ALERT_CHANGE.by} ({ALERT_CHANGE.role}), {ALERT_CHANGE.date}.</> : null}
      </div>
    </>
  );
}

// ── placeholder ──

function Placeholder({ title }: { title: string }) {
  return (
    <section className="setd-card setd-placeholder">
      <SectionHeader n="" title={title} />
      <p style={{ marginTop: 12 }}>
        This settings page is being rebuilt in the new design. Its controls are unchanged in the meantime.
      </p>
    </section>
  );
}

// ── shell ──

export interface SettingsShellProps {
  slug: RcmSettingsSlug;
  /** On /settings/payers/:payerId: that payer's page replaces the list. */
  payerId?: string;
  slots?: Partial<Record<RcmSettingsSlug, React.ReactNode>>;
}

/** A viewer (not owner/admin) reads settings only. Mock: ?role=viewer. */
function readIsViewer(): boolean {
  try {
    return new URLSearchParams(window.location.search).get('role') === 'viewer';
  } catch {
    return false;
  }
}

/** Band save line for the Alerts page, driven by the mock autosave state. */
function AlertsBandSave() {
  const s = useAlertsSaveState();
  if (s === 'saving') return <div className="setd-save">Saving…</div>;
  if (s === 'error') {
    return (
      <div className="setd-save">
        <span className="setd-save-dot" style={{ background: 'var(--amber)' }} aria-hidden />
        Couldn&rsquo;t save. Try again
      </div>
    );
  }
  return (
    <div className="setd-save">
      <span className="setd-save-dot" aria-hidden />
      Changes save as you make them
    </div>
  );
}

export function SettingsShell({ slug, payerId, slots }: SettingsShellProps) {
  const meta = metaFor(slug);
  const item = itemFor(slug);
  const isViewer = readIsViewer();

  let body: React.ReactNode;
  if (slug === 'autopilot') body = <AutopilotPage />;
  else if (slug === 'alerts') body = <AlertsPage readOnly={isViewer} />;
  else if (item?.render === 'slot' && slots?.[slug]) body = slots[slug];
  else if (slug === 'payers' && payerId) body = <PayerDetailPage payerId={payerId} />;
  else body = renderSettingsPage(slug) ?? <Placeholder title={meta.title} />;

  return (
    <div className="setd">
      <div className="setd-cols">
        <SubNav active={slug} />
        <div className="setd-main">
          {/* Like the Core settings pages: the page title sits at the top of the right-hand column, beside the menu. */}
          <header className="setd-head">
            <p className="setd-kicker">Settings · {meta.group}</p>
            <h1 className="setd-h1">{meta.title}</h1>
            {meta.lead ? <p className="setd-lead">{meta.lead}</p> : null}
            {slug === 'alerts' && !isViewer ? (
              <AlertsBandSave />
            ) : (
              <div className="setd-save">
                {meta.bandLine ? null : <span className="setd-save-dot" aria-hidden />}
                {meta.bandLine ?? 'Changes save as you make them'}
              </div>
            )}
          </header>
          {body}
        </div>
      </div>
    </div>
  );
}
